import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { MarketplaceStoreSelectorComponent } from '../../shared/design-system/marketplace-store-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StorefrontPlacementKeySelectorComponent } from '../../shared/design-system/storefront-placement-key-selector.component';
import { CatalogService, StorefrontContentBlock } from './catalog.service';
import { STOREFRONT_CONTENT_FORMATS } from './marketplace-storefront-selectors.model';
import {
  MARKETPLACE_LIST_TOOLBAR_STYLES,
  MarketplaceLiveSubscription,
  compareSortValues,
  downloadCsv,
  liveStatusKey,
  subscribeMarketplaceOperationsLiveUpdates,
  toggleSortField,
  unsubscribeMarketplaceOperationsLiveUpdates
} from './marketplace-list-page.utils';

type ContentBlockSortField = 'placementKey' | 'title' | 'status' | 'displayOrder';

@Component({
  standalone: true,
  selector: 'ain-storefront-content-block-manager',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinDatePipe,
    JalaliDateInputComponent,
    MarketplaceStoreSelectorComponent,
    StorefrontPlacementKeySelectorComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TextareaModule
  ],
  template: `
    <ain-page
      [title]="t('catalog.storefront.content.title')"
      [description]="t('catalog.storefront.content.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'catalog.marketplace.help.list.title'"
        [descriptionKey]="'catalog.marketplace.help.list.description'"
        [bulletKeys]="[
          'catalog.marketplace.help.list.bullets.selection',
          'catalog.marketplace.help.list.bullets.filters',
          'catalog.marketplace.help.list.bullets.live'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.storefront.content.create')" (click)="openDialog()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.bulk')" (click)="applyBulk()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.export')" (click)="exportRows()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusKey(liveUpdateService.connectionState())) }}
            </span>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('catalog.marketplace.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'catalog.marketplace.help.filters.title'"
                [descriptionKey]="'catalog.marketplace.help.filters.description'"
                [bulletKeys]="['catalog.marketplace.help.filters.bullets.search']" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('catalog.marketplace.filters.store') }}</span>
                <ain-marketplace-store-selector
                  [(ngModel)]="selectedStoreId"
                  (ngModelChange)="onStoreChange()" />
              </label>
              <label>
                <span>{{ t('catalog.marketplace.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
            </div>
          </details>

          @if (!selectedStoreId) {
            <ain-state type="empty" [title]="t('catalog.storefront.selectStore')" />
          } @else if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('catalog.storefront.empty')" />
          } @else {
            <p-table
              [value]="filteredRows()"
              dataKey="id"
              [paginator]="true"
              [rows]="15"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th><button type="button" class="sort-btn" [class.active]="sortField() === 'placementKey'" (click)="sortBy('placementKey')">{{ t('catalog.storefront.content.fields.placementKey') }}</button></th>
                  <th><button type="button" class="sort-btn" [class.active]="sortField() === 'title'" (click)="sortBy('title')">{{ t('columns.title') }}</button></th>
                  <th><button type="button" class="sort-btn" [class.active]="sortField() === 'status'" (click)="sortBy('status')">{{ t('columns.status') }}</button></th>
                  <th>{{ t('catalog.storefront.content.fields.window') }}</th>
                  <th><button type="button" class="sort-btn" [class.active]="sortField() === 'displayOrder'" (click)="sortBy('displayOrder')">{{ t('catalog.storefront.publishing.order') }}</button></th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td>{{ placementKeyLabel(row.placementKey) }}</td>
                  <td>{{ row.title }}</td>
                  <td>{{ t(statusKey(row.status)) }}</td>
                  <td>{{ row.visibleFromUtc ? (row.visibleFromUtc | ainDate) : '—' }} / {{ row.visibleToUtc ? (row.visibleToUtc | ainDate) : '—' }}</td>
                  <td>{{ row.displayOrder }}</td>
                  <td class="actions">
                    <button pButton type="button" size="small" [label]="t('catalog.storefront.publishing.publish')" (click)="openActionDialog('publish', row.id)"></button>
                    <button pButton type="button" size="small" severity="danger" [label]="t('catalog.storefront.publishing.archive')" (click)="openActionDialog('archive', row.id)"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (info()) { <p class="info">{{ info() }}</p> }
        </section>
      }

      <p-dialog [header]="t('catalog.storefront.content.create')" [(visible)]="dialogVisible" [modal]="true" [style]="{ width: '560px' }">
        <div class="form">
          <label>
            <span>{{ t('catalog.marketplace.filters.store') }}</span>
            <ain-marketplace-store-selector [(ngModel)]="dialogStoreId" class="full" />
          </label>
          <label>{{ t('catalog.storefront.content.fields.placementKey') }}</label>
          <ain-storefront-placement-key-selector [(ngModel)]="placementKey" [scope]="'content'" class="full" />
          <label>{{ t('columns.title') }}</label>
          <input pInputText [(ngModel)]="title" class="full" />
          <label>{{ t('catalog.storefront.content.fields.body') }}</label>
          <textarea pTextarea [(ngModel)]="body" rows="4" class="full"></textarea>
          <label>{{ t('columns.type') }}</label>
          <p-select
            [options]="formatOptions"
            optionValue="value"
            [(ngModel)]="format"
            [placeholder]="t('catalog.selectors.contentFormat.placeholder')"
            appendTo="body"
            class="full">
            <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '' }}</ng-template>
            <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
          </p-select>
          <label>{{ t('catalog.storefront.publishing.visibleFrom') }}</label>
          <ain-date-input [(ngModel)]="visibleFromUtc" class="full" />
          <label>{{ t('catalog.storefront.publishing.visibleTo') }}</label>
          <ain-date-input [(ngModel)]="visibleToUtc" class="full" />
          <label>{{ t('catalog.storefront.publishing.order') }}</label>
          <input pInputText type="number" [(ngModel)]="displayOrder" class="full" />
          @if (dialogError()) { <p class="error">{{ dialogError() }}</p> }
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeDialog()"></button>
          <button pButton type="button" [label]="t('catalog.action.save')" [disabled]="acting()" (click)="save()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog
        [header]="pendingAction() === 'archive' ? t('catalog.marketplace.storefront.confirm.archiveTitle') : t('catalog.marketplace.storefront.confirm.publishTitle')"
        [(visible)]="actionDialogVisible"
        [modal]="true"
        [style]="{ width: '440px' }">
        <label class="form">
          <span>{{ t('catalog.marketplace.storefront.confirm.reason') }}</span>
          <textarea pTextarea [(ngModel)]="actionReason" rows="3" class="full"></textarea>
        </label>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="actionDialogVisible = false"></button>
          <button
            pButton
            type="button"
            [severity]="pendingAction() === 'archive' ? 'danger' : undefined"
            [label]="pendingAction() === 'archive' ? t('catalog.storefront.publishing.archive') : t('catalog.storefront.publishing.publish')"
            [disabled]="acting() || !actionReason.trim()"
            (click)="confirmAction()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .form { display: grid; gap: 10px; }
    .error { color: #b91c1c; font-size: 13px; margin: 0; }
    ${MARKETPLACE_LIST_TOOLBAR_STYLES}
  `]
})
export class StorefrontContentBlockManagerComponent implements OnInit, OnDestroy {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private marketplaceLive: MarketplaceLiveSubscription = { topic: null, eventsSubscription: null };

  readonly loading = signal(true);
  readonly acting = signal(false);
  readonly info = signal('');
  readonly rows = signal<StorefrontContentBlock[]>([]);
  readonly selectedRows = signal<StorefrontContentBlock[]>([]);
  readonly search = signal('');
  readonly sortField = signal<ContentBlockSortField>('displayOrder');
  readonly sortOrder = signal<1 | -1>(1);
  readonly pendingAction = signal<'publish' | 'archive' | null>(null);

  liveStatusKey = liveStatusKey;

  readonly formatOptions = STOREFRONT_CONTENT_FORMATS.map(value => ({
    value,
    labelKey: `catalog.selectors.contentFormat.${value}`
  }));

  selectedStoreId: string | null = null;
  dialogStoreId: string | null = null;
  readonly dialogError = signal('');
  dialogVisible = false;
  actionDialogVisible = false;
  pendingTargetId = '';
  actionReason = '';
  placementKey: string | null = 'footer.trust';
  title = '';
  body = '';
  format = 'Markdown';
  visibleFromUtc: string | null = null;
  visibleToUtc: string | null = null;
  displayOrder = 10;

  ngOnInit(): void {
    this.search.set(this.route.snapshot.queryParamMap.get('q') ?? '');
    this.loading.set(false);
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    void unsubscribeMarketplaceOperationsLiveUpdates(this.liveUpdateService, this.marketplaceLive);
  }

  onStoreChange(): void {
    this.selectedRows.set([]);
    this.load();
  }

  load(): void {
    if (!this.selectedStoreId) {
      this.loading.set(false);
      this.rows.set([]);
      return;
    }

    this.loading.set(true);
    this.info.set('');
    this.service.listStorefrontContentBlocks(this.selectedStoreId).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.loading.set(false);
      }
    });
  }

  filteredRows(): StorefrontContentBlock[] {
    const value = this.search().trim().toLowerCase();
    const filtered = !value
      ? this.rows()
      : this.rows().filter(row =>
        row.placementKey.toLowerCase().includes(value)
        || row.title.toLowerCase().includes(value)
        || row.status.toLowerCase().includes(value));

    const field = this.sortField();
    const order = this.sortOrder();
    return [...filtered].sort((left, right) => compareSortValues(left[field], right[field]) * order);
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.search().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  onSelectionChange(selection: StorefrontContentBlock[] | StorefrontContentBlock | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  sortBy(field: ContentBlockSortField): void {
    toggleSortField(
      this.sortField(),
      field,
      this.sortOrder(),
      value => this.sortField.set(value),
      value => this.sortOrder.set(value)
    );
  }

  applyBulk(): void {
    if (this.selectedRows().length === 0) {
      this.info.set(this.t('catalog.marketplace.actions.bulkNothingSelected'));
      return;
    }
    this.info.set(this.t('catalog.marketplace.actions.bulkQueued'));
  }

  exportRows(): void {
    const rows = this.filteredRows();
    if (rows.length === 0) {
      this.info.set(this.t('catalog.marketplace.actions.exportEmpty'));
      return;
    }
    downloadCsv(
      'storefront-content-blocks.csv',
      ['PlacementKey', 'Title', 'Status', 'VisibleFromUtc', 'VisibleToUtc', 'DisplayOrder'],
      rows.map(row => [
        this.placementKeyLabel(row.placementKey),
        row.title,
        this.t(this.statusKey(row.status)),
        row.visibleFromUtc ?? '',
        row.visibleToUtc ?? '',
        row.displayOrder.toString()
      ]));
    this.info.set(this.t('catalog.marketplace.actions.exportReady'));
  }

  openDialog(): void {
    this.dialogStoreId = this.selectedStoreId;
    this.placementKey = 'footer.trust';
    this.title = '';
    this.body = '';
    this.format = 'Markdown';
    this.visibleFromUtc = null;
    this.visibleToUtc = null;
    this.displayOrder = (this.rows().length + 1) * 10;
    this.dialogError.set('');
    this.dialogVisible = true;
  }

  closeDialog(): void {
    this.dialogVisible = false;
    this.dialogError.set('');
  }

  save(): void {
    this.dialogError.set('');
    const storeId = this.dialogStoreId?.trim() || this.selectedStoreId?.trim();
    const placementKey = this.placementKey?.trim();
    const title = this.title.trim();
    if (!storeId) {
      this.dialogError.set(this.t('catalog.storefront.publishing.validation.storeRequired'));
      return;
    }
    if (!placementKey) {
      this.dialogError.set(this.t('catalog.storefront.publishing.validation.placementKeyRequired'));
      return;
    }
    if (!title) {
      this.dialogError.set(this.t('catalog.storefront.publishing.validation.titleRequired'));
      return;
    }

    this.acting.set(true);
    this.service.upsertStorefrontContentBlock({
      storeId,
      placementKey,
      title,
      body: this.body ?? '',
      contentFormat: this.format || 'Markdown',
      visibleFromUtc: this.normalizeDateValue(this.visibleFromUtc),
      visibleToUtc: this.normalizeDateValue(this.visibleToUtc),
      displayOrder: this.parseDisplayOrder(this.displayOrder)
    }).subscribe({
      next: () => {
        this.selectedStoreId = storeId;
        this.closeDialog();
        this.load();
        this.acting.set(false);
      },
      error: () => {
        this.dialogError.set(this.t('catalog.storefront.publishing.validation.saveFailed'));
        this.acting.set(false);
      }
    });
  }

  private parseDisplayOrder(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : 10;
  }

  private normalizeDateValue(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  }

  openActionDialog(action: 'publish' | 'archive', id: string): void {
    this.pendingAction.set(action);
    this.pendingTargetId = id;
    this.actionReason = '';
    this.actionDialogVisible = true;
  }

  confirmAction(): void {
    if (!this.pendingTargetId || !this.actionReason.trim()) {
      this.info.set(this.t('catalog.marketplace.storefront.confirm.reasonRequired'));
      return;
    }

    this.acting.set(true);
    const request$ = this.pendingAction() === 'archive'
      ? this.service.archiveStorefrontContentBlock(this.pendingTargetId)
      : this.service.publishStorefrontContentBlock(this.pendingTargetId);

    request$.subscribe({
      next: () => {
        this.actionDialogVisible = false;
        this.pendingAction.set(null);
        this.pendingTargetId = '';
        this.actionReason = '';
        this.load();
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  statusKey(status: string): string {
    return `catalog.marketplace.storefront.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  placementKeyLabel(key: string): string {
    return this.t(`catalog.selectors.placementKey.${key.replace(/\./g, '_')}`);
  }

  private async startLiveUpdates(): Promise<void> {
    this.marketplaceLive = await subscribeMarketplaceOperationsLiveUpdates(
      this.currentContext.context()?.tenantId,
      this.liveUpdateService,
      () => this.load()
    );
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
